import { useEffect, useMemo, useRef, useState } from "react";

const EMPTY_FORM = {
  organization_name: "",
  website: "",
  country: "France",
  sector: "Distribution automobile / VO",
  structure_type: "groupe",
  points_of_sale: "",
  employees: "",
  vo_stock: "",
  vo_sales_monthly: "",
  contact_name: "",
  contact_role: "",
  contact_email: "",
  contact_phone: "",
  contact_linkedin: "",
  signal_level: "signal_public",
  signal_source: "",
  signal_description: "",
  qualification_status: "a_qualifier",
  ai_maturity: "faible",
  current_tools: "",
  sourcing_process: "",
  pricing_process: "",
  lead_process: "",
  workflow_process: "",
  data_process: "",
  pain_points: [],
  pain_details: "",
  business_goals: "",
  priority_need: "",
  urgency: "moyenne",
  decision_horizon: "",
  budget_context: "",
  economic_avg_stock_purchase_price: "",
  economic_units_over_target: "",
  economic_excess_stock_days: "",
  economic_carrying_rate_pct: "",
  economic_admin_hours_week: "",
  economic_hourly_cost: "",
  economic_lost_leads_month: "",
  economic_lead_conversion_pct: "",
  economic_gross_margin_vehicle: "",
  economic_margin_leak_units_month: "",
  economic_margin_leak_per_vehicle: "",
  economic_viq_annual_cost: "",
  economic_time_recovery_pct: "25",
  economic_stock_recovery_pct: "20",
  economic_lead_recovery_pct: "20",
  economic_margin_recovery_pct: "15",
  economic_notes: "",
  training_need: "a_evaluer",
  training_participants: "",
  training_audience: "",
  training_goals: "",
  free_notes: "",
};

const PAIN_OPTIONS = [
  ["pilotage", "Pilotage VO / vision consolidée"],
  ["stocks", "Stocks / rotation / dormants"],
  ["sourcing", "Sourcing / recherche d'opportunités"],
  ["pricing", "Pricing / décote / positionnement marché"],
  ["data", "Data / indicateurs / reporting"],
  ["leads", "Leads / CRM / relances"],
  ["workflow", "Workflow / priorisation / tâches"],
  ["coordination", "Coordination multi-sites / équipe"],
  ["digitalisation", "Digitalisation / outils fragmentés"],
  ["ia", "Adoption de l'IA métier"],
  ["dossiers", "Dossiers VO / standardisation"],
  ["integrations", "Mails / agenda / contacts / intégrations"],
];

const SIGNAL_LABELS = {
  signal_public: "Signal public / marché",
  contact_identifie: "Interlocuteur identifié",
  echange_direct: "Échange direct réalisé",
  besoin_confirme: "Besoin confirmé",
  rendez_vous: "Rendez-vous prévu",
  proposition: "Proposition / devis en cours",
};

const STATUS_LABELS = {
  a_qualifier: "À qualifier",
  qualifie: "Qualifié",
  prioritaire: "Prioritaire",
  nurturing: "À maturer",
  rdv: "RDV / démonstration",
  proposition: "Proposition envoyée",
};

const RESEARCH_FIELD_LABELS = {
  website:"Site web", country:"Pays", sector:"Secteur", structure_type:"Type de structure", points_of_sale:"Points de vente",
  employees:"Effectif", vo_stock:"Stock VO", vo_sales_monthly:"Ventes VO / mois", contact_name:"Interlocuteur", contact_role:"Fonction",
  contact_email:"Email professionnel", contact_phone:"Téléphone professionnel", contact_linkedin:"LinkedIn", signal_source:"Source du signal",
  signal_description:"Contexte public", current_tools:"Outils actuels", sourcing_process:"Sourcing", pricing_process:"Pricing", lead_process:"Leads / CRM",
  workflow_process:"Workflow", data_process:"Data / reporting",
};
const RESEARCH_NUMERIC_FIELDS = new Set(["points_of_sale","employees","vo_stock","vo_sales_monthly"]);

function formatResearchValue(field, value) {
  if (RESEARCH_NUMERIC_FIELDS.has(field)) {
    const n = Number(String(value ?? "").replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? String(n) : String(value ?? "");
  }
  return String(value ?? "").trim();
}

const levelClass = (score) => score >= 80 ? "is-very-high" : score >= 65 ? "is-high" : score >= 45 ? "is-medium" : "is-low";
const scoreLabel = (score) => score >= 80 ? "Très forte" : score >= 65 ? "Forte" : score >= 45 ? "Modérée" : "Faible";
const numberOrBlank = (v) => (v === null || v === undefined || v === "") ? "" : String(v);
const formatEur = (value, fallback = "—") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
};

function cleanForm(input = {}) {
  return {
    ...EMPTY_FORM,
    ...input,
    points_of_sale: numberOrBlank(input.points_of_sale),
    employees: numberOrBlank(input.employees),
    vo_stock: numberOrBlank(input.vo_stock),
    vo_sales_monthly: numberOrBlank(input.vo_sales_monthly),
    training_participants: numberOrBlank(input.training_participants),
    economic_avg_stock_purchase_price: numberOrBlank(input.economic_avg_stock_purchase_price),
    economic_units_over_target: numberOrBlank(input.economic_units_over_target),
    economic_excess_stock_days: numberOrBlank(input.economic_excess_stock_days),
    economic_carrying_rate_pct: numberOrBlank(input.economic_carrying_rate_pct),
    economic_admin_hours_week: numberOrBlank(input.economic_admin_hours_week),
    economic_hourly_cost: numberOrBlank(input.economic_hourly_cost),
    economic_lost_leads_month: numberOrBlank(input.economic_lost_leads_month),
    economic_lead_conversion_pct: numberOrBlank(input.economic_lead_conversion_pct),
    economic_gross_margin_vehicle: numberOrBlank(input.economic_gross_margin_vehicle),
    economic_margin_leak_units_month: numberOrBlank(input.economic_margin_leak_units_month),
    economic_margin_leak_per_vehicle: numberOrBlank(input.economic_margin_leak_per_vehicle),
    economic_viq_annual_cost: numberOrBlank(input.economic_viq_annual_cost),
    economic_time_recovery_pct: numberOrBlank(input.economic_time_recovery_pct ?? EMPTY_FORM.economic_time_recovery_pct),
    economic_stock_recovery_pct: numberOrBlank(input.economic_stock_recovery_pct ?? EMPTY_FORM.economic_stock_recovery_pct),
    economic_lead_recovery_pct: numberOrBlank(input.economic_lead_recovery_pct ?? EMPTY_FORM.economic_lead_recovery_pct),
    economic_margin_recovery_pct: numberOrBlank(input.economic_margin_recovery_pct ?? EMPTY_FORM.economic_margin_recovery_pct),
    pain_points: Array.isArray(input.pain_points) ? input.pain_points : [],
  };
}

function Field({ label, children, hint, span = 1 }) {
  return (
    <label className={`aff-qual-field ${span === 2 ? "span-2" : ""}`}>
      <span className="aff-qual-label">{label}</span>
      {children}
      {hint && <span className="aff-qual-hint">{hint}</span>}
    </label>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="aff-qual-section">
      <div className="aff-qual-section-head">
        <div className="aff-qual-section-title">{title}</div>
        {subtitle && <div className="aff-qual-section-sub">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function StatCard({ label, score, detail }) {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div className={`aff-qual-stat ${levelClass(n)}`}>
      <div className="aff-qual-stat-label">{label}</div>
      <div className="aff-qual-stat-line"><strong>{n}</strong><span>/100</span></div>
      <div className="aff-qual-stat-detail">{detail || scoreLabel(n)}</div>
    </div>
  );
}

function reportToText(lead, report) {
  if (!report) return "";
  const lines = [];
  lines.push(`DIAGNOSTIC DE PERTINENCE VIQ.AI — ${lead?.organization_name || "Structure"}`);
  lines.push("");
  lines.push(`Pertinence VIQ.AI : ${report.viq_score || 0}/100`);
  lines.push(`Pertinence Formation : ${report.training_score || 0}/100`);
  lines.push(`Qualification du lead : ${report.qualification_score || 0}/100`);
  if (report.recommended_offer) lines.push(`Orientation : ${report.recommended_offer}`);
  lines.push("");
  if (report.executive_summary) {
    lines.push("SYNTHÈSE");
    lines.push(report.executive_summary);
    lines.push("");
  }
  if (Array.isArray(report.key_findings) && report.key_findings.length) {
    lines.push("POINTS CLÉS");
    report.key_findings.forEach(x => lines.push(`- ${x}`));
    lines.push("");
  }
  if (report.economic_analysis) {
    const eco = report.economic_analysis;
    lines.push("DIMENSION ÉCONOMIQUE");
    if (eco.has_data) {
      lines.push(`Pertes potentielles quantifiées : ${formatEur(eco.annual_potential_loss)} / an`);
      lines.push(`Gain VIQ.AI estimatif : ${formatEur(eco.annual_gain_central)} / an (fourchette ${formatEur(eco.annual_gain_low)} à ${formatEur(eco.annual_gain_high)})`);
      if (eco.annual_viq_cost > 0) {
        lines.push(`Coût VIQ.AI renseigné : ${formatEur(eco.annual_viq_cost)} / an`);
        lines.push(`Gain net central estimatif : ${formatEur(eco.net_gain_central)} / an`);
        if (eco.roi_pct != null) lines.push(`ROI central estimatif : ${eco.roi_pct}%`);
        if (eco.payback_months != null) lines.push(`Retour sur investissement estimatif : ${eco.payback_months} mois`);
      }
      (eco.loss_components || []).forEach(c => lines.push(`- ${c.label} : ${formatEur(c.annual_amount)} / an — ${c.basis}`));
      lines.push(`Hypothèse centrale de gains : ${(eco.gain_components || []).map(c => `${c.label} ${c.recovery_pct}%`).join(" ; ")}`);
    } else {
      lines.push("Données économiques insuffisantes pour chiffrer les pertes et gains potentiels.");
    }
    if (eco.interpretation) lines.push(eco.interpretation);
    if (Array.isArray(eco.missing_inputs) && eco.missing_inputs.length) lines.push(`À compléter : ${eco.missing_inputs.join(" ; ")}`);
    lines.push("");
  }
  if (Array.isArray(report.modules) && report.modules.length) {
    lines.push("PERTINENCE PAR MODULE");
    report.modules.forEach(m => {
      lines.push(`- ${m.name} — ${m.score}/100 (${m.level || scoreLabel(m.score)})`);
      if (m.rationale) lines.push(`  ${m.rationale}`);
      if (Array.isArray(m.use_cases) && m.use_cases.length) lines.push(`  Cas d'usage : ${m.use_cases.join(" ; ")}`);
    });
    lines.push("");
  }
  if (report.approach_angle) {
    lines.push("ANGLE D'APPROCHE");
    lines.push(report.approach_angle);
    lines.push("");
  }
  if (report.training_recommendation) {
    lines.push("RECOMMANDATION FORMATION");
    lines.push(report.training_recommendation);
    lines.push("");
  }
  if (Array.isArray(report.discovery_questions) && report.discovery_questions.length) {
    lines.push("QUESTIONS À VALIDER");
    report.discovery_questions.forEach(x => lines.push(`- ${x}`));
    lines.push("");
  }
  if (Array.isArray(report.risks) && report.risks.length) {
    lines.push("POINTS DE VIGILANCE");
    report.risks.forEach(x => lines.push(`- ${x}`));
  }
  return lines.join("\n");
}

function printReport(lead, report) {
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
  const modules = Array.isArray(report?.modules) ? report.modules : [];
  const eco = report?.economic_analysis || null;
  const list = (arr) => Array.isArray(arr) && arr.length ? `<ul>${arr.map(x => `<li>${esc(x)}</li>`).join("")}</ul>` : "";
  const w = window.open("", "_blank", "noopener,noreferrer,width=980,height=760");
  if (!w) return false;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Diagnostic VIQ.AI — ${esc(lead?.organization_name || "Lead")}</title><style>
  body{font-family:Inter,Arial,sans-serif;color:#15202b;margin:34px;line-height:1.5}.brand{font-size:22px;font-weight:800}.orange{color:#ff5b16}h1{font-size:26px;margin:28px 0 8px}h2{font-size:17px;margin-top:28px;border-bottom:1px solid #e6dfd3;padding-bottom:7px}.scores{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:20px 0}.score{background:#f7f1e8;border-radius:12px;padding:14px}.score strong{font-size:24px}.module{break-inside:avoid;border:1px solid #e6dfd3;border-radius:10px;padding:12px;margin:10px 0}.module-head{display:flex;justify-content:space-between;gap:12px;font-weight:700}.muted{color:#66717d;font-size:13px}.eco-kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:14px 0}.eco-kpi{border:1px solid #e6dfd3;border-radius:10px;padding:11px;background:#fffaf4}.eco-kpi span{display:block;font-size:11px;color:#66717d}.eco-kpi strong{font-size:18px}.econ-table{width:100%;border-collapse:collapse;font-size:12px}.econ-table td,.econ-table th{border-bottom:1px solid #eee6dc;padding:7px;text-align:left}.footer{margin-top:28px;color:#7a838c;font-size:11px}@media print{body{margin:14mm}.no-print{display:none}}</style></head><body>
  <div class="brand">✦ Vehicle<span class="orange">IQ</span> · VIQ.AI</div>
  <h1>Diagnostic de pertinence — ${esc(lead?.organization_name || "Structure")}</h1>
  <div class="muted">Rapport généré le ${new Date().toLocaleDateString("fr-FR")} · Les informations non renseignées sont traitées comme des points à confirmer, jamais comme des faits.</div>
  <div class="scores"><div class="score"><div>Pertinence VIQ.AI</div><strong>${esc(report?.viq_score || 0)}/100</strong></div><div class="score"><div>Pertinence Formation</div><strong>${esc(report?.training_score || 0)}/100</strong></div><div class="score"><div>Qualification lead</div><strong>${esc(report?.qualification_score || 0)}/100</strong></div></div>
  <h2>Synthèse</h2><p>${esc(report?.executive_summary || "")}</p>
  ${report?.recommended_offer ? `<p><strong>Orientation :</strong> ${esc(report.recommended_offer)}</p>` : ""}
  <h2>Points clés</h2>${list(report?.key_findings)}
  <h2>Dimension économique</h2>${eco?.has_data ? `<div class="eco-kpis"><div class="eco-kpi"><span>Pertes potentielles quantifiées</span><strong>${esc(formatEur(eco.annual_potential_loss))}/an</strong></div><div class="eco-kpi"><span>Gain VIQ.AI central estimatif</span><strong>${esc(formatEur(eco.annual_gain_central))}/an</strong></div><div class="eco-kpi"><span>Fourchette de gain</span><strong>${esc(formatEur(eco.annual_gain_low))} – ${esc(formatEur(eco.annual_gain_high))}</strong></div></div><table class="econ-table"><thead><tr><th>Poste</th><th>Perte potentielle/an</th><th>Récupération centrale</th><th>Gain estimatif/an</th></tr></thead><tbody>${(eco.loss_components || []).map(c => { const g=(eco.gain_components||[]).find(x=>x.id===c.id)||{}; return `<tr><td>${esc(c.label)}</td><td>${esc(formatEur(c.annual_amount))}</td><td>${esc(g.recovery_pct ?? "—")}${g.recovery_pct != null ? "%" : ""}</td><td>${esc(formatEur(g.annual_gain))}</td></tr>`; }).join("")}</tbody></table>${eco.annual_viq_cost>0 ? `<p><strong>Coût VIQ.AI renseigné :</strong> ${esc(formatEur(eco.annual_viq_cost))}/an · <strong>Gain net central :</strong> ${esc(formatEur(eco.net_gain_central))}/an${eco.roi_pct!=null ? ` · <strong>ROI estimatif :</strong> ${esc(eco.roi_pct)}%` : ""}${eco.payback_months!=null ? ` · <strong>Retour estimatif :</strong> ${esc(eco.payback_months)} mois` : ""}</p>` : ""}<p>${esc(eco.interpretation || "")}</p><div class="muted">Scénario indicatif fondé sur les données et hypothèses saisies ; il ne constitue ni une garantie de gain ni une prévision financière.</div>` : `<p>Données économiques insuffisantes pour chiffrer les pertes et gains potentiels.</p>${list(eco?.missing_inputs)}`}
  <h2>Pertinence par module</h2>${modules.map(m => `<div class="module"><div class="module-head"><span>${esc(m.name)}</span><span>${esc(m.score)}/100 · ${esc(m.level || scoreLabel(m.score))}</span></div><p>${esc(m.rationale || "")}</p>${list(m.use_cases)}</div>`).join("")}
  <h2>Angle d'approche</h2><p>${esc(report?.approach_angle || "")}</p>
  <h2>Formation</h2><p>${esc(report?.training_recommendation || "")}</p>
  <h2>Questions à valider</h2>${list(report?.discovery_questions)}
  <h2>Points de vigilance</h2>${list(report?.risks)}
  <div class="footer">VehicleIQ · Diagnostic d'aide à la qualification commerciale. Le rapport s'appuie uniquement sur les informations saisies et les hypothèses explicitement signalées.</div>
  <script>window.onload=()=>setTimeout(()=>window.print(),250)<\/script></body></html>`);
  w.document.close();
  return true;
}

export default function AffiliateLeadQualification({ apiGet, apiPost, user }) {
  const [leads, setLeads] = useState([]);
  const [scope, setScope] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [report, setReport] = useState(null);
  const [view, setView] = useState("form");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [twilioOk, setTwilioOk] = useState(null);
  const [callState, setCallState] = useState(null);
  const [integrationSummary, setIntegrationSummary] = useState({ connected:0, mail:{relevant:0,unread:0}, agenda:{relevant_today:0,today:0}, contacts:{total:0} });
  const [webResearch, setWebResearch] = useState(null);
  const [researching, setResearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [bulkResearching, setBulkResearching] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importSelection, setImportSelection] = useState(new Set());
  const importFileRef = useRef(null);

  const selected = useMemo(() => leads.find(x => String(x.id) === String(selectedId)) || null, [leads, selectedId]);

  const reload = async (preferredId = null) => {
    setLoading(true);
    try {
      const r = await apiGet("/api/affiliate/qualified-leads");
      const rows = r?.success ? (r.leads || []) : [];
      setLeads(rows);
      setScope(r?.scope || null);
      if (preferredId != null) setSelectedId(preferredId);
    } catch (e) {
      setError(e?.message || "Impossible de charger les qualifications.");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // v320 — Même click-to-call CRM que dans Prospection client. Si Twilio
  // n'est pas disponible pour ce compte, on conserve le lien tel: natif.
  useEffect(() => {
    let alive = true;
    apiGet("/api/voice/direct-call/status")
      .then(r => { if (alive) setTwilioOk(!!r?.configured); })
      .catch(() => { if (alive) setTwilioOk(false); });
    return () => { alive = false; };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // v332 — Le résumé d'intégrations est affiché ici, dans la qualification,
  // et n'encombre plus l'écran d'accueil VIQ.AI.
  useEffect(() => {
    let alive = true;
    Promise.all([
      apiGet("/api/integrations/status").catch(() => null),
      apiGet("/api/integrations/workspace/summary").catch(() => null),
    ]).then(([status, summary]) => {
      if (!alive) return;
      const connected = status?.success ? Object.values(status.connections || {}).filter(x => x?.status === "connected").length : 0;
      const data = summary?.success ? (summary.summary || {}) : {};
      setIntegrationSummary({
        connected,
        mail:data.mail || {relevant:0,unread:0},
        agenda:data.agenda || {relevant_today:0,today:0},
        contacts:data.contacts || {total:0},
      });
    });
    return () => { alive = false; };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) return;
    setForm(cleanForm(selected.form_data || selected));
    setReport(selected.diagnostic || null);
    setWebResearch(selected.web_research || null);
    setView(selected.diagnostic ? "report" : "form");
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const togglePain = (id) => setForm(prev => ({ ...prev, pain_points: prev.pain_points.includes(id) ? prev.pain_points.filter(x => x !== id) : [...prev.pain_points, id] }));

  const newLead = () => {
    setSelectedId(null);
    setForm(EMPTY_FORM);
    setReport(null);
    setWebResearch(null);
    setCallState(null);
    setMessage(""); setError(""); setView("form");
  };

  const save = async ({ quiet = false } = {}) => {
    setSaving(true); setError(""); if (!quiet) setMessage("");
    try {
      if (!form.organization_name.trim()) throw new Error("Renseignez le nom de la structure.");
      const path = selectedId ? `/api/affiliate/qualified-leads/${selectedId}` : "/api/affiliate/qualified-leads";
      const payload = { form_data: form };
      const r = selectedId ? await apiPost(path, { ...payload, _method: "PUT" }) : await apiPost(path, payload);
      if (!r?.success || !r?.lead?.id) throw new Error(r?.error || "Enregistrement impossible.");
      const id = r.lead.id;
      setSelectedId(id);
      setReport(r.lead.diagnostic || null);
      setWebResearch(r.lead.web_research || webResearch || null);
      if (!r.lead.diagnostic && !quiet) setView("form");
      setLeads(prev => {
        const next = [r.lead, ...prev.filter(x => String(x.id) !== String(id))];
        return next.sort((a,b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      });
      try { window.dispatchEvent(new CustomEvent("viq-crm-leads-refresh", { detail:{ source:"qualification_lead", id:r.lead.crm_lead_id || null } })); } catch (_) {}
      if (!quiet) setMessage("Qualification enregistrée et synchronisée dans les Leads CRM.");
      return r.lead;
    } catch (e) {
      setError(e?.message || "Enregistrement impossible.");
      return null;
    } finally { setSaving(false); }
  };

  const contactEmail = () => {
    const email = String(form.contact_email || "").trim();
    if (!email) return;
    const subject = `VehicleIQ / VIQ.AI — ${form.organization_name || "prise de contact"}`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
  };

  const contactCall = async () => {
    const phone = String(form.contact_phone || "").trim();
    if (!phone) return;
    if (twilioOk === false) {
      window.location.href = `tel:${phone}`;
      return;
    }
    setCallState("calling"); setError(""); setMessage("");
    try {
      // Sauvegarder d'abord garantit la projection dans le CRM et fournit un
      // target_id CRM stable pour la note d'appel automatique.
      const saved = await save({ quiet: true });
      if (!saved?.id) throw new Error("Enregistrez d'abord la qualification.");
      const r = await apiPost("/api/voice/direct-call", {
        phone,
        contact_name: form.contact_name || form.organization_name || "Lead qualifié",
        target_id: String(saved.crm_lead_id || selected?.crm_lead_id || saved.id),
        target_type: "lead",
      });
      if (!r?.success) throw new Error(r?.error || "Appel impossible.");
      setCallState("done");
      setMessage("Appel CRM lancé : décrochez, VehicleIQ vous met en relation avec le lead.");
    } catch (e) {
      setCallState(e?.message || "Appel impossible.");
      setError(e?.message || "Appel impossible.");
    }
  };


  const researchWeb = async () => {
    setResearching(true); setError(""); setMessage("");
    try {
      let id = selectedId;
      if (!id) {
        const saved = await save({ quiet:true });
        id = saved?.id || null;
      }
      if (!id) throw new Error("Enregistrez d'abord le lead avant le recoupement web.");
      const r = await apiPost(`/api/affiliate/qualified-leads/${id}/research-web`, {});
      if (!r?.success || !r?.research) throw new Error(r?.error || "Recoupement web impossible.");
      setWebResearch(r.research);
      setLeads(prev => prev.map(x => String(x.id) === String(id) ? { ...x, web_research:r.research, web_researched_at:r.research.researched_at, updated_at:r.lead?.updated_at || x.updated_at } : x));
      setMessage(`${Object.keys(r.research.fields || {}).length} complément(s) potentiel(s) trouvé(s) à partir des sources publiques.`);
    } catch (e) {
      setError(e?.message || "Recoupement web impossible.");
    } finally { setResearching(false); }
  };

  const applyResearch = async () => {
    const fields = webResearch?.fields || {};
    const updates = {};
    Object.entries(fields).forEach(([key, item]) => {
      if (!RESEARCH_FIELD_LABELS[key] || String(form[key] ?? "").trim() !== "") return;
      const confidence = Number(item?.confidence || 0);
      if (confidence < 0.8) return;
      const value = formatResearchValue(key, item?.value);
      if (value) updates[key] = value;
    });
    if (!Object.keys(updates).length) {
      setMessage("Aucun complément suffisamment fiable ne peut être appliqué automatiquement aux champs vides.");
      return;
    }
    setForm(prev => ({ ...prev, ...updates }));
    setMessage(`${Object.keys(updates).length} champ(s) complété(s). Enregistrez la qualification pour conserver les modifications.`);
  };

  const researchIncompleteLeads = async () => {
    const targets = leads.filter(lead => !lead.web_research && String((lead.form_data || {}).organization_name || lead.organization_name || "").trim()).slice(0, 20);
    if (!targets.length) { setMessage("Tous les leads disposent déjà d'un recoupement web."); return; }
    if (!window.confirm(`Lancer le recoupement web sur ${targets.length} lead${targets.length > 1 ? "s" : ""} ? Une requête IA est consommée par lead.`)) return;
    setBulkResearching(true); setError(""); setMessage("");
    let done = 0;
    try {
      for (const lead of targets) {
        const r = await apiPost(`/api/affiliate/qualified-leads/${lead.id}/research-web`, {});
        if (!r?.success) throw new Error(r?.error || "Recoupement impossible.");
        done++;
        setLeads(prev => prev.map(x => String(x.id) === String(lead.id) ? { ...x, web_research:r.research, web_researched_at:r.research?.researched_at } : x));
        if (String(selectedId) === String(lead.id)) setWebResearch(r.research);
      }
      setMessage(`${done} lead${done > 1 ? "s" : ""} recoupé${done > 1 ? "s" : ""} avec les sources web publiques.`);
    } catch (e) {
      setError(`${done} lead${done > 1 ? "s" : ""} recoupé${done > 1 ? "s" : ""}. ${e?.message || "Le recoupement a été interrompu."}`);
    } finally { setBulkResearching(false); }
  };

  const openImportPicker = () => importFileRef.current?.click();
  const onImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const supported = /\.(csv|tsv|xlsx|json)$/i.test(file.name);
    if (!supported) { setError("Formats acceptés : CSV, TSV, XLSX ou JSON."); return; }
    if (file.size > 12 * 1024 * 1024) { setError("Le fichier ne doit pas dépasser 12 Mo."); return; }
    setImporting(true); setError(""); setMessage("");
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Lecture du fichier impossible."));
        reader.readAsDataURL(file);
      });
      const r = await apiPost("/api/affiliate/qualified-leads/import", { mode:"preview", filename:file.name, data });
      if (!r?.success) throw new Error(r?.error || "Prévisualisation impossible.");
      const validRows = (r.rows || []).filter(x => x.valid);
      setImportPreview({ ...r, filename:file.name });
      setImportSelection(new Set(validRows.map(x => x.row_number)));
    } catch (e) {
      setError(e?.message || "Import impossible.");
    } finally { setImporting(false); }
  };

  const commitImport = async () => {
    if (!importPreview) return;
    const rows = (importPreview.rows || []).filter(x => x.valid && importSelection.has(x.row_number));
    if (!rows.length) { setError("Sélectionnez au moins un lead valide à importer."); return; }
    setImporting(true); setError(""); setMessage("");
    try {
      const r = await apiPost("/api/affiliate/qualified-leads/import", { mode:"commit", rows });
      if (!r?.success) throw new Error(r?.error || "Import impossible.");
      setImportPreview(null); setImportSelection(new Set());
      await reload(r.imported?.[0]?.id || null);
      if (r.imported?.[0]?.id) setSelectedId(r.imported[0].id);
      try { window.dispatchEvent(new CustomEvent("viq-crm-leads-refresh", { detail:{ source:"qualification_import" } })); } catch (_) {}
      setMessage(`${r.imported_count || 0} lead(s) importé(s)${r.skipped_count ? ` · ${r.skipped_count} doublon(s) ignoré(s)` : ""}${r.error_count ? ` · ${r.error_count} ligne(s) en erreur` : ""}. Lancez ensuite « Recouper avec le web » sur les leads à compléter.`);
    } catch (e) {
      setError(e?.message || "Import impossible.");
    } finally { setImporting(false); }
  };

  const diagnose = async () => {
    setDiagnosing(true); setError(""); setMessage("");
    try {
      const saved = await save({ quiet: true });
      if (!saved?.id) return;
      const r = await apiPost(`/api/affiliate/qualified-leads/${saved.id}/diagnose`, {});
      if (!r?.success || !r?.diagnostic) throw new Error(r?.error || "Diagnostic impossible.");
      setReport(r.diagnostic);
      setLeads(prev => prev.map(x => String(x.id) === String(saved.id) ? { ...x, diagnostic: r.diagnostic, updated_at: r.lead?.updated_at || x.updated_at } : x));
      setView("report");
      setMessage(r.diagnostic.ai_generated ? "Diagnostic VIQ.AI généré et enregistré." : "Diagnostic généré et enregistré (mode de secours sans IA externe)." );
    } catch (e) {
      setError(e?.message || "Diagnostic impossible.");
    } finally { setDiagnosing(false); }
  };

  const remove = async () => {
    if (!selectedId || !window.confirm(`Supprimer la qualification « ${form.organization_name || "ce lead"} » ?`)) return;
    setSaving(true); setError("");
    try {
      const r = await apiPost(`/api/affiliate/qualified-leads/${selectedId}`, { _method: "DELETE" });
      if (!r?.success) throw new Error(r?.error || "Suppression impossible.");
      setLeads(prev => prev.filter(x => String(x.id) !== String(selectedId)));
      newLead();
      setMessage("Qualification supprimée.");
    } catch (e) { setError(e?.message || "Suppression impossible."); }
    finally { setSaving(false); }
  };

  const copy = async () => {
    const text = reportToText(form, report);
    try { await navigator.clipboard.writeText(text); setMessage("Rapport copié dans le presse-papiers."); }
    catch (_) { setError("Copie automatique impossible sur ce navigateur."); }
  };

  const inputClass = "input aff-qual-input";
  const modules = Array.isArray(report?.modules) ? [...report.modules].sort((a,b) => Number(b.score || 0) - Number(a.score || 0)) : [];

  return (
    <div className="affiliate-qualification-root">
      <div className="aff-qual-intro">
        <div>
          <div className="aff-qual-kicker">VIQ.AI · qualification commerciale</div>
          <h3>Qualifier un lead et mesurer la pertinence VehicleIQ</h3>
          <p>Centralisez les faits, le signal métier, les irritants, la maturité IA et les indicateurs économiques. Le diagnostic distingue les informations confirmées des hypothèses, évalue chaque module et chiffre les pertes potentielles ainsi que le gain VIQ.AI dans un scénario transparent.</p>
        </div>
        <div className="aff-qual-intro-actions">
          <input ref={importFileRef} type="file" accept=".csv,.tsv,.xlsx,.json" onChange={onImportFile} hidden />
          <button type="button" className="btn btn-outline btn-sm" onClick={openImportPicker} disabled={importing || bulkResearching}>⇧ Importer un fichier</button>
          {leads.some(x => !x.web_research) && <button type="button" className="btn btn-outline btn-sm" onClick={researchIncompleteLeads} disabled={importing || bulkResearching}>{bulkResearching ? "Recoupement en cours…" : "⌕ Recouper les incomplets"}</button>}
          <button type="button" className="btn btn-accent btn-sm" onClick={newLead}>＋ Nouveau lead</button>
        </div>
      </div>

      <div className="aff-qual-integration-banner">
        <div className="aff-qual-integration-copy">
          <span className="aff-qual-kicker">Contexte des intégrations</span>
          <strong>{(() => {
            const connected=Number(integrationSummary.connected||0), mail=Number(integrationSummary.mail?.relevant||0), unread=Number(integrationSummary.mail?.unread||0), agenda=Number(integrationSummary.agenda?.relevant_today||integrationSummary.agenda?.today||0), contacts=Number(integrationSummary.contacts?.total||0);
            const parts=[];
            if(mail>0) parts.push(`${mail} email${mail>1?"s":""} VO pertinent${mail>1?"s":""}${unread>0?` (${unread} non lu${unread>1?"s":""})`:""}`);
            if(agenda>0) parts.push(`${agenda} événement${agenda>1?"s":""} VO aujourd’hui`);
            if(contacts>0) parts.push(`${contacts} contact${contacts>1?"s":""} synchronisé${contacts>1?"s":""}`);
            if(parts.length) return `Vos intégrations remontent ${parts.join(" · ")}.`;
            if(connected>0) return `${connected} intégration${connected>1?"s":""} connectée${connected>1?"s":""} : vos emails, agendas et contacts sont synchronisés.`;
            return "Connectez vos emails, agendas et contacts pour que VIQ.AI centralise automatiquement les signaux utiles.";
          })()}</strong>
        </div>
        <span className="aff-qual-integration-note">Les données affichées ici servent de contexte : elles ne sont pas injectées automatiquement dans la fiche sans validation.</span>
      </div>

      {scope?.shared && (
        <div className="aff-qual-team-banner">
          <div><strong>👥 Qualification partagée avec l'équipe</strong><span>{scope.organization_name || "Organisation VehicleIQ"}{scope.members_count ? ` · ${scope.members_count} membre${scope.members_count > 1 ? "s" : ""}` : ""}</span></div>
          <p>Les leads, modifications et diagnostics de cet onglet sont visibles par les membres de la même équipe. L'auteur et la dernière mise à jour restent identifiables.</p>
        </div>
      )}

      <div className="affiliate-qualification-layout">
        <aside className="aff-qual-list">
          <div className="aff-qual-list-title">Qualifications <span>{leads.length}</span></div>
          {loading ? <div className="aff-qual-empty">Chargement…</div> : leads.length === 0 ? (
            <div className="aff-qual-empty">Aucun lead qualifié enregistré. Créez le premier diagnostic.</div>
          ) : leads.map(lead => {
            const active = String(lead.id) === String(selectedId);
            const f = lead.form_data || {};
            return (
              <button type="button" key={lead.id} className={`aff-qual-lead-card ${active ? "is-active" : ""}`} onClick={() => setSelectedId(lead.id)}>
                <div className="aff-qual-lead-name">{lead.organization_name || f.organization_name || "Structure"}</div>
                <div className="aff-qual-lead-meta">{f.contact_name || lead.contact_name || "Interlocuteur à confirmer"}</div>
                {scope?.shared && <div className="aff-qual-lead-author">{lead.updated_by_uid === user?.uid ? "Modifié par vous" : `Modifié par ${lead.updated_by_name || lead.created_by_name || "un membre de l'équipe"}`}</div>}
                <div className="aff-qual-lead-bottom">
                  <span>{STATUS_LABELS[f.qualification_status] || "À qualifier"}</span>
                  <span className="aff-qual-lead-signals">{lead.web_research ? "⌕ Web recoupé" : ""}{lead.diagnostic?.viq_score != null ? ` · ${lead.diagnostic.viq_score}/100` : ""}</span>
                </div>
              </button>
            );
          })}
        </aside>

        <main className="aff-qual-main">
          <div className="aff-qual-toolbar">
            <div className="aff-qual-view-tabs">
              <button type="button" className={view === "form" ? "active" : ""} onClick={() => setView("form")}>Qualification</button>
              <button type="button" className={view === "report" ? "active" : ""} onClick={() => setView("report")} disabled={!report}>Diagnostic VIQ.AI</button>
            </div>
            <div className="aff-qual-actions">
              {selectedId && <button type="button" className="btn btn-outline btn-sm" onClick={researchWeb} disabled={saving || diagnosing || researching}>{researching ? "Recherche web…" : "⌕ Recouper avec le web"}</button>}
              {selectedId && <button type="button" className="btn btn-ghost btn-sm" onClick={remove} disabled={saving || diagnosing || researching}>Supprimer</button>}
              <button type="button" className="btn btn-outline btn-sm" onClick={() => save()} disabled={saving || diagnosing || researching}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
              <button type="button" className="btn btn-accent btn-sm" onClick={diagnose} disabled={saving || diagnosing || researching || !form.organization_name.trim()}>{diagnosing ? "Analyse VIQ.AI…" : "✦ Générer le diagnostic"}</button>
            </div>
          </div>

          {(message || error) && <div className={`aff-qual-feedback ${error ? "is-error" : "is-success"}`}>{error || message}</div>}

          {webResearch && view === "form" && (
            <section className="aff-qual-web-research">
              <div className="aff-qual-web-research-head">
                <div><div className="aff-qual-kicker">Recoupement web public</div><strong>Informations trouvées pour compléter la fiche</strong><span>{webResearch.researched_at ? ` · ${new Date(webResearch.researched_at).toLocaleString("fr-FR")}` : ""}</span></div>
                <div className="aff-qual-web-research-actions"><button type="button" className="btn btn-outline btn-sm" onClick={researchWeb} disabled={researching}>{researching ? "Recherche…" : "↻ Actualiser"}</button><button type="button" className="btn btn-accent btn-sm" onClick={applyResearch} disabled={researching}>Appliquer les compléments sûrs</button></div>
              </div>
              {webResearch.summary && <p className="aff-qual-web-research-summary">{webResearch.summary}</p>}
              {!!Object.keys(webResearch.fields || {}).length && <div className="aff-qual-research-suggestions">{Object.entries(webResearch.fields).map(([key,item]) => {
                const current=String(form[key] ?? "").trim(); const confidence=Math.round(Number(item?.confidence||0)*100);
                return <div key={key} className="aff-qual-research-suggestion">
                  <div><strong>{RESEARCH_FIELD_LABELS[key] || key}</strong><span className={current ? "is-filled" : confidence >= 80 ? "is-safe" : "is-uncertain"}>{current ? "Déjà renseigné" : `${confidence}% de confiance`}</span></div>
                  <div className="aff-qual-research-values"><span className="current">{current || "Champ vide"}</span><span className="arrow">→</span><span className="proposed">{formatResearchValue(key,item?.value)}</span></div>
                  {!!item?.evidence?.length && <div className="aff-qual-research-evidence">Source{item.evidence.length>1?"s":""} : {item.evidence.map((n,i) => <a key={i} href={webResearch.sources?.[Number(n)-1]?.url || "#"} target="_blank" rel="noreferrer">{webResearch.sources?.[Number(n)-1]?.source_domain || `#${n}`}</a>)}</div>}
                </div>;
              })}</div>}
              {!!(webResearch.sources || []).length && <div className="aff-qual-web-sources"><strong>Sources consultées</strong><div>{webResearch.sources.slice(0,8).map((source,i)=><a key={i} href={source.url} target="_blank" rel="noreferrer"><span>{source.source_domain || "web"}</span>{source.title || source.url}</a>)}</div></div>}
            </section>
          )}

          {importPreview && (
            <div className="aff-qual-import-panel">
              <div className="aff-qual-import-head"><div><div className="aff-qual-kicker">Import de leads</div><strong>{importPreview.filename}</strong><span>{importPreview.valid_count} ligne(s) prête(s) · {importPreview.invalid_count} à corriger</span></div><button type="button" className="btn btn-ghost btn-sm" onClick={() => {setImportPreview(null);setImportSelection(new Set());}}>Fermer</button></div>
              {importPreview.unmapped_headers?.length > 0 && <div className="aff-qual-import-warning">Colonnes non reconnues : {importPreview.unmapped_headers.join(", ")}. Elles ne seront pas importées automatiquement.</div>}
              <div className="aff-qual-import-toolbar"><label><input type="checkbox" checked={importPreview.valid_count > 0 && (importPreview.rows||[]).filter(x=>x.valid).every(x=>importSelection.has(x.row_number))} onChange={e => setImportSelection(e.target.checked ? new Set((importPreview.rows||[]).filter(x=>x.valid).map(x=>x.row_number)) : new Set())} /> Sélectionner les lignes valides</label><span>{importSelection.size} sélectionnée(s) sur {importPreview.valid_count}</span><button type="button" className="btn btn-accent btn-sm" onClick={commitImport} disabled={importing || !importSelection.size}>{importing ? "Import…" : `Importer ${importSelection.size} lead${importSelection.size>1?"s":""}`}</button></div>
              <div className="aff-qual-import-table-wrap"><table className="aff-qual-import-table"><thead><tr><th></th><th>Ligne</th><th>Structure</th><th>Interlocuteur</th><th>Email</th><th>Site</th><th>Pays</th><th>État</th></tr></thead><tbody>{(importPreview.rows||[]).map(row => <tr key={row.row_number} className={row.valid ? "" : "is-invalid"}><td>{row.valid && <input type="checkbox" checked={importSelection.has(row.row_number)} onChange={e => setImportSelection(prev => { const next=new Set(prev); e.target.checked ? next.add(row.row_number) : next.delete(row.row_number); return next; })} />}</td><td>#{row.row_number}</td><td><strong>{row.form_data?.organization_name || "—"}</strong></td><td>{row.form_data?.contact_name || "—"}</td><td>{row.form_data?.contact_email || "—"}</td><td>{row.form_data?.website || "—"}</td><td>{row.form_data?.country || "—"}</td><td>{row.valid ? <span className="aff-qual-import-ok">Prêt</span> : <span className="aff-qual-import-error">{row.missing?.join(", ") || "À corriger"}</span>}</td></tr>)}</tbody></table></div>
              {importPreview.processed_rows < importPreview.total_rows && <div className="aff-qual-import-note">Les {importPreview.total_rows - importPreview.processed_rows} ligne(s) au-delà de la limite de 250 n'ont pas été prévisualisées.</div>}
            </div>
          )}

          {view === "form" && (
            <div className="aff-qual-form">
              <Section title="1. Structure" subtitle="Dimension, organisation et volumétrie VO.">
                <div className="aff-qual-grid">
                  <Field label="Structure *"><input className={inputClass} value={form.organization_name} onChange={e => update("organization_name", e.target.value)} placeholder="Ex. Groupe automobile / marchand VO" /></Field>
                  <Field label="Site / URL"><input className={inputClass} value={form.website} onChange={e => update("website", e.target.value)} placeholder="https://…" /></Field>
                  <Field label="Secteur"><input className={inputClass} value={form.sector} onChange={e => update("sector", e.target.value)} /></Field>
                  <Field label="Type de structure"><select className={inputClass} value={form.structure_type} onChange={e => update("structure_type", e.target.value)}><option value="independant">Indépendant</option><option value="groupe">Groupe / réseau</option><option value="concession">Concession</option><option value="marchand">Marchand VO</option><option value="plateforme">Plateforme / service VO</option><option value="autre">Autre</option></select></Field>
                  <Field label="Pays"><input className={inputClass} value={form.country} onChange={e => update("country", e.target.value)} /></Field>
                  <Field label="Points de vente / sites"><input type="number" min="0" className={inputClass} value={form.points_of_sale} onChange={e => update("points_of_sale", e.target.value)} placeholder="Ex. 130" /></Field>
                  <Field label="Effectif concerné"><input type="number" min="0" className={inputClass} value={form.employees} onChange={e => update("employees", e.target.value)} placeholder="Équipe VO / utilisateurs potentiels" /></Field>
                  <Field label="Stock VO"><input type="number" min="0" className={inputClass} value={form.vo_stock} onChange={e => update("vo_stock", e.target.value)} placeholder="Nombre de VO en stock" /></Field>
                  <Field label="Ventes VO / mois"><input type="number" min="0" className={inputClass} value={form.vo_sales_monthly} onChange={e => update("vo_sales_monthly", e.target.value)} placeholder="Volume mensuel estimé" /></Field>
                </div>
              </Section>

              <Section title="2. Interlocuteur & signal" subtitle="Tracez la source du lead et le niveau réel de preuve commerciale.">
                <div className="aff-qual-grid">
                  <Field label="Interlocuteur"><input className={inputClass} value={form.contact_name} onChange={e => update("contact_name", e.target.value)} placeholder="Nom et prénom" /></Field>
                  <Field label="Fonction"><input className={inputClass} value={form.contact_role} onChange={e => update("contact_role", e.target.value)} placeholder="DG, Directeur VO, responsable occasion…" /></Field>
                  <Field label="Email"><input type="email" className={inputClass} value={form.contact_email} onChange={e => update("contact_email", e.target.value)} /></Field>
                  <Field label="Téléphone"><input className={inputClass} value={form.contact_phone} onChange={e => update("contact_phone", e.target.value)} /></Field>
                  <Field label="LinkedIn"><input className={inputClass} value={form.contact_linkedin} onChange={e => update("contact_linkedin", e.target.value)} placeholder="Lien du profil" /></Field>
                  <Field label="Statut de qualification"><select className={inputClass} value={form.qualification_status} onChange={e => update("qualification_status", e.target.value)}>{Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
                  <Field label="Niveau de signal"><select className={inputClass} value={form.signal_level} onChange={e => update("signal_level", e.target.value)}>{Object.entries(SIGNAL_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
                  <Field label="Source du signal"><input className={inputClass} value={form.signal_source} onChange={e => update("signal_source", e.target.value)} placeholder="Offre d'emploi, échange, actualité, salon, recommandation…" /></Field>
                  <Field label="Signal / contexte observé" span={2} hint="Décrivez uniquement ce qui est connu ou observé. Le rapport séparera ces faits des hypothèses."><textarea className={inputClass} rows="4" value={form.signal_description} onChange={e => update("signal_description", e.target.value)} placeholder="Ex. création d'une direction VO Groupe, volonté de centraliser stocks, pricing, data et IA…" /></Field>
                </div>
                {(form.contact_phone || form.contact_email) && (
                  <div style={{ display:"flex", gap:".55rem", flexWrap:"wrap", alignItems:"center", marginTop:".75rem", padding:".7rem .8rem", border:"1px solid var(--border)", borderRadius:10, background:"var(--card)" }}>
                    <span style={{ fontSize:".76rem", color:"var(--sub)", marginRight:".15rem" }}>Contacter ce lead :</span>
                    {form.contact_phone && (twilioOk === false ? (
                      <a className="btn btn-outline btn-sm" href={`tel:${form.contact_phone}`} style={{ textDecoration:"none" }}>📞 Appeler</a>
                    ) : (
                      <button type="button" className="btn btn-outline btn-sm" onClick={contactCall} disabled={saving || callState === "calling"}>
                        {callState === "calling" ? "Connexion…" : callState === "done" ? "✓ Appel lancé" : "📞 Appeler via CRM"}
                      </button>
                    ))}
                    {form.contact_email && <button type="button" className="btn btn-outline btn-sm" onClick={contactEmail}>✉️ Envoyer un email</button>}
                    {selected?.crm_lead_id && <span style={{ fontSize:".7rem", color:"var(--muted)" }}>Lead CRM #{selected.crm_lead_id}</span>}
                  </div>
                )}
              </Section>

              <Section title="3. Fonctionnement actuel" subtitle="Comprendre où se situent les ruptures de flux et les ressaisies.">
                <div className="aff-qual-grid">
                  <Field label="Maturité IA"><select className={inputClass} value={form.ai_maturity} onChange={e => update("ai_maturity", e.target.value)}><option value="aucune">Aucune / inconnue</option><option value="faible">Faible — usages individuels</option><option value="intermediaire">Intermédiaire — quelques cas métier</option><option value="avancee">Avancée — processus déjà structurés</option></select></Field>
                  <Field label="Outils actuels"><input className={inputClass} value={form.current_tools} onChange={e => update("current_tools", e.target.value)} placeholder="DMS, CRM, Excel, Gmail, Outlook, outils sourcing…" /></Field>
                  <Field label="Sourcing" span={2}><textarea className={inputClass} rows="3" value={form.sourcing_process} onChange={e => update("sourcing_process", e.target.value)} placeholder="Comment les véhicules sont-ils recherchés et les opportunités priorisées ?" /></Field>
                  <Field label="Pricing / valorisation" span={2}><textarea className={inputClass} rows="3" value={form.pricing_process} onChange={e => update("pricing_process", e.target.value)} placeholder="Comment le prix d'achat / revente est-il décidé ? Quels outils ?" /></Field>
                  <Field label="Leads / CRM / relances"><textarea className={inputClass} rows="3" value={form.lead_process} onChange={e => update("lead_process", e.target.value)} placeholder="Réception, qualification, relances, appels…" /></Field>
                  <Field label="Workflow / coordination"><textarea className={inputClass} rows="3" value={form.workflow_process} onChange={e => update("workflow_process", e.target.value)} placeholder="Tâches, agenda, assignations, multi-sites…" /></Field>
                  <Field label="Data / reporting" span={2}><textarea className={inputClass} rows="3" value={form.data_process} onChange={e => update("data_process", e.target.value)} placeholder="Indicateurs suivis, fréquence du reporting, niveau de consolidation…" /></Field>
                </div>
              </Section>

              <Section title="4. Irritants, objectifs & décision" subtitle="Le diagnostic transforme ces éléments en cas d'usage VehicleIQ concrets.">
                <div className="aff-qual-pains">
                  {PAIN_OPTIONS.map(([id,label]) => <button key={id} type="button" className={form.pain_points.includes(id) ? "is-selected" : ""} onClick={() => togglePain(id)}>{form.pain_points.includes(id) ? "✓ " : "+ "}{label}</button>)}
                </div>
                <div className="aff-qual-grid" style={{ marginTop: ".85rem" }}>
                  <Field label="Précisions sur les irritants" span={2}><textarea className={inputClass} rows="3" value={form.pain_details} onChange={e => update("pain_details", e.target.value)} /></Field>
                  <Field label="Objectifs métier" span={2}><textarea className={inputClass} rows="3" value={form.business_goals} onChange={e => update("business_goals", e.target.value)} placeholder="Réduire le temps, mieux tourner le stock, homogénéiser le réseau, mieux exploiter la data…" /></Field>
                  <Field label="Besoin prioritaire"><input className={inputClass} value={form.priority_need} onChange={e => update("priority_need", e.target.value)} placeholder="Le point à résoudre en premier" /></Field>
                  <Field label="Urgence"><select className={inputClass} value={form.urgency} onChange={e => update("urgency", e.target.value)}><option value="faible">Faible</option><option value="moyenne">Moyenne</option><option value="forte">Forte</option><option value="immediate">Immédiate</option></select></Field>
                  <Field label="Horizon de décision"><input className={inputClass} value={form.decision_horizon} onChange={e => update("decision_horizon", e.target.value)} placeholder="Ex. T4, 3 mois, après recrutement…" /></Field>
                  <Field label="Contexte budget / achat"><input className={inputClass} value={form.budget_context} onChange={e => update("budget_context", e.target.value)} placeholder="Budget identifié, appel d'offres, test, inconnu…" /></Field>
                </div>
              </Section>

              <Section title="5. Dimension économique" subtitle="Chiffrez uniquement ce que vous pouvez documenter. Les hypothèses de récupération VIQ.AI sont éditables et restent indicatives.">
                <div className="aff-qual-econ-note">
                  <strong>Principe :</strong> le diagnostic calcule d'abord les pertes potentielles actuelles, puis applique des hypothèses de récupération explicites pour obtenir une fourchette de gain. Aucun montant n'est inventé si les données de base sont absentes.
                </div>
                <div className="aff-qual-econ-group">
                  <div className="aff-qual-econ-group-title">Stock & rotation</div>
                  <div className="aff-qual-grid">
                    <Field label="Valeur d'achat moyenne d'un VO (€)"><input type="number" min="0" step="100" className={inputClass} value={form.economic_avg_stock_purchase_price} onChange={e => update("economic_avg_stock_purchase_price", e.target.value)} placeholder="Ex. 18 000" /></Field>
                    <Field label="VO concernés par un dépassement / mois"><input type="number" min="0" step="1" className={inputClass} value={form.economic_units_over_target} onChange={e => update("economic_units_over_target", e.target.value)} placeholder="Ex. 25" /></Field>
                    <Field label="Dépassement moyen (jours)"><input type="number" min="0" step="1" className={inputClass} value={form.economic_excess_stock_days} onChange={e => update("economic_excess_stock_days", e.target.value)} placeholder="Ex. 30" /></Field>
                    <Field label="Coût annuel de portage du stock (%)" hint="Financement + coûts de détention estimés par la structure."><input type="number" min="0" max="100" step="0.1" className={inputClass} value={form.economic_carrying_rate_pct} onChange={e => update("economic_carrying_rate_pct", e.target.value)} placeholder="Ex. 9" /></Field>
                  </div>
                </div>
                <div className="aff-qual-econ-group">
                  <div className="aff-qual-econ-group-title">Temps & productivité</div>
                  <div className="aff-qual-grid">
                    <Field label="Heures administratives / ressaisies par semaine"><input type="number" min="0" step="0.5" className={inputClass} value={form.economic_admin_hours_week} onChange={e => update("economic_admin_hours_week", e.target.value)} placeholder="Ex. 12" /></Field>
                    <Field label="Coût horaire chargé (€)"><input type="number" min="0" step="1" className={inputClass} value={form.economic_hourly_cost} onChange={e => update("economic_hourly_cost", e.target.value)} placeholder="Ex. 35" /></Field>
                  </div>
                </div>
                <div className="aff-qual-econ-group">
                  <div className="aff-qual-econ-group-title">Leads & marge</div>
                  <div className="aff-qual-grid">
                    <Field label="Leads perdus / non traités par mois"><input type="number" min="0" step="1" className={inputClass} value={form.economic_lost_leads_month} onChange={e => update("economic_lost_leads_month", e.target.value)} placeholder="Ex. 15" /></Field>
                    <Field label="Taux de transformation attendu (%)"><input type="number" min="0" max="100" step="0.1" className={inputClass} value={form.economic_lead_conversion_pct} onChange={e => update("economic_lead_conversion_pct", e.target.value)} placeholder="Ex. 12" /></Field>
                    <Field label="Marge brute moyenne / vente (€)"><input type="number" min="0" step="50" className={inputClass} value={form.economic_gross_margin_vehicle} onChange={e => update("economic_gross_margin_vehicle", e.target.value)} placeholder="Ex. 2 000" /></Field>
                    <Field label="VO avec fuite de marge / mois"><input type="number" min="0" step="1" className={inputClass} value={form.economic_margin_leak_units_month} onChange={e => update("economic_margin_leak_units_month", e.target.value)} placeholder="Ex. 8" /></Field>
                    <Field label="Fuite de marge moyenne / VO (€)"><input type="number" min="0" step="25" className={inputClass} value={form.economic_margin_leak_per_vehicle} onChange={e => update("economic_margin_leak_per_vehicle", e.target.value)} placeholder="Ex. 250" /></Field>
                    <Field label="Coût VIQ.AI annuel envisagé (€)" hint="Optionnel : permet d'estimer gain net, ROI et délai de retour."><input type="number" min="0" step="100" className={inputClass} value={form.economic_viq_annual_cost} onChange={e => update("economic_viq_annual_cost", e.target.value)} placeholder="Ex. montant du devis" /></Field>
                  </div>
                </div>
                <div className="aff-qual-econ-group is-assumptions">
                  <div className="aff-qual-econ-group-title">Hypothèses centrales de récupération VIQ.AI</div>
                  <div className="aff-qual-grid">
                    <Field label="Temps récupérable (%)" hint="Défaut 25 %, modifiable."><input type="number" min="0" max="100" step="1" className={inputClass} value={form.economic_time_recovery_pct} onChange={e => update("economic_time_recovery_pct", e.target.value)} /></Field>
                    <Field label="Coût de stock évitable (%)" hint="Défaut 20 %, modifiable."><input type="number" min="0" max="100" step="1" className={inputClass} value={form.economic_stock_recovery_pct} onChange={e => update("economic_stock_recovery_pct", e.target.value)} /></Field>
                    <Field label="Leads récupérables (%)" hint="Défaut 20 %, modifiable."><input type="number" min="0" max="100" step="1" className={inputClass} value={form.economic_lead_recovery_pct} onChange={e => update("economic_lead_recovery_pct", e.target.value)} /></Field>
                    <Field label="Fuite de marge évitable (%)" hint="Défaut 15 %, modifiable."><input type="number" min="0" max="100" step="1" className={inputClass} value={form.economic_margin_recovery_pct} onChange={e => update("economic_margin_recovery_pct", e.target.value)} /></Field>
                    <Field label="Notes / source des hypothèses" span={2}><textarea className={inputClass} rows="3" value={form.economic_notes} onChange={e => update("economic_notes", e.target.value)} placeholder="Ex. reporting interne, atelier métier, estimation du responsable VO…" /></Field>
                  </div>
                </div>
              </Section>

              <Section title="6. Formation IA" subtitle="Évaluer l'accompagnement nécessaire pour l'adoption, au-delà du seul logiciel.">
                <div className="aff-qual-grid">
                  <Field label="Besoin formation"><select className={inputClass} value={form.training_need} onChange={e => update("training_need", e.target.value)}><option value="a_evaluer">À évaluer</option><option value="oui">Oui / exprimé</option><option value="non">Non</option></select></Field>
                  <Field label="Participants potentiels"><input type="number" min="0" max="200" className={inputClass} value={form.training_participants} onChange={e => update("training_participants", e.target.value)} /></Field>
                  <Field label="Public concerné"><input className={inputClass} value={form.training_audience} onChange={e => update("training_audience", e.target.value)} placeholder="Direction, acheteurs, commerciaux, responsables VO…" /></Field>
                  <Field label="Objectifs pédagogiques"><input className={inputClass} value={form.training_goals} onChange={e => update("training_goals", e.target.value)} placeholder="Adoption IA, prompts métier, sourcing, data, workflows…" /></Field>
                  <Field label="Notes complémentaires" span={2}><textarea className={inputClass} rows="4" value={form.free_notes} onChange={e => update("free_notes", e.target.value)} placeholder="Informations utiles qui ne rentrent pas dans les champs ci-dessus." /></Field>
                </div>
              </Section>
            </div>
          )}

          {view === "report" && report && (
            <div className="aff-qual-report">
              <div className="aff-qual-report-head">
                <div>
                  <div className="aff-qual-kicker">Rapport de pertinence</div>
                  <h3>{form.organization_name || "Structure"}</h3>
                  <div className="aff-qual-report-meta">{SIGNAL_LABELS[form.signal_level] || "Signal à préciser"} · {STATUS_LABELS[form.qualification_status] || "À qualifier"}{report.generated_at ? ` · ${new Date(report.generated_at).toLocaleString("fr-FR")}` : ""}{scope?.shared && selected ? ` · ${selected.updated_by_uid === user?.uid ? "mis à jour par vous" : `mis à jour par ${selected.updated_by_name || selected.created_by_name || "l'équipe"}`}` : ""}</div>
                </div>
                <div className="aff-qual-report-actions"><button type="button" className="btn btn-outline btn-sm" onClick={copy}>Copier</button><button type="button" className="btn btn-outline btn-sm" onClick={() => printReport(form, report)}>Imprimer / PDF</button></div>
              </div>

              <div className="aff-qual-stats">
                <StatCard label="Pertinence VIQ.AI" score={report.viq_score} detail={report.viq_fit_label} />
                <StatCard label="Pertinence Formation" score={report.training_score} detail={report.training_fit_label} />
                <StatCard label="Qualification lead" score={report.qualification_score} detail={report.qualification_label} />
              </div>

              <div className="aff-qual-report-block is-highlight">
                <div className="aff-qual-report-title">Synthèse exécutive</div>
                <p>{report.executive_summary}</p>
                {report.recommended_offer && <div className="aff-qual-reco"><span>Orientation proposée</span><strong>{report.recommended_offer}</strong></div>}
              </div>

              <div className="aff-qual-report-block">
                <div className="aff-qual-report-title">Fiche structure</div>
                <div className="aff-qual-profile-grid">
                  {[
                    ["Secteur", form.sector || "À confirmer"],
                    ["Type", form.structure_type || "À confirmer"],
                    ["Points de vente", form.points_of_sale || "À confirmer"],
                    ["Stock VO", form.vo_stock || "À confirmer"],
                    ["Ventes VO / mois", form.vo_sales_monthly || "À confirmer"],
                    ["Maturité IA", form.ai_maturity || "À confirmer"],
                    ["Interlocuteur", form.contact_name ? `${form.contact_name}${form.contact_role ? ` · ${form.contact_role}` : ""}` : "À confirmer"],
                    ["Signal", SIGNAL_LABELS[form.signal_level] || "À confirmer"],
                  ].map(([k,v]) => <div key={k}><span>{k}</span><strong>{v}</strong></div>)}
                </div>
              </div>

              {!!report.key_findings?.length && <div className="aff-qual-report-block"><div className="aff-qual-report-title">Points clés de la structure</div><ul>{report.key_findings.map((x,i) => <li key={i}>{x}</li>)}</ul></div>}

              {report.economic_analysis && (
                <div className="aff-qual-report-block aff-qual-economics">
                  <div className="aff-qual-report-title">Dimension économique · pertes potentielles & gain VIQ.AI</div>
                  {report.economic_analysis.has_data ? (
                    <>
                      <div className="aff-qual-economic-kpis">
                        <div><span>Pertes potentielles quantifiées</span><strong>{formatEur(report.economic_analysis.annual_potential_loss)}<small>/an</small></strong><em>{formatEur(report.economic_analysis.monthly_potential_loss)}/mois</em></div>
                        <div className="is-positive"><span>Gain VIQ.AI central estimatif</span><strong>{formatEur(report.economic_analysis.annual_gain_central)}<small>/an</small></strong><em>{formatEur(report.economic_analysis.monthly_gain_central)}/mois</em></div>
                        <div><span>Fourchette de gain estimatif</span><strong>{formatEur(report.economic_analysis.annual_gain_low)} – {formatEur(report.economic_analysis.annual_gain_high)}</strong><em>scénario bas → haut</em></div>
                      </div>
                      <div className="aff-qual-economic-table-wrap">
                        <table className="aff-qual-economic-table">
                          <thead><tr><th>Poste</th><th>Perte potentielle/an</th><th>Hypothèse VIQ.AI</th><th>Gain central/an</th></tr></thead>
                          <tbody>{(report.economic_analysis.loss_components || []).map((c) => {
                            const gain = (report.economic_analysis.gain_components || []).find(x => x.id === c.id) || {};
                            return <tr key={c.id}><td><strong>{c.label}</strong><span>{c.basis}</span>{!!c.viq_levers?.length && <span className="aff-qual-economic-levers">Leviers VIQ.AI : {c.viq_levers.join(" · ")}</span>}</td><td>{formatEur(c.annual_amount)}</td><td>{gain.recovery_pct != null ? `${gain.recovery_pct}%` : "—"}</td><td>{formatEur(gain.annual_gain)}</td></tr>;
                          })}</tbody>
                        </table>
                      </div>
                      {Number(report.economic_analysis.annual_viq_cost || 0) > 0 && (
                        <div className="aff-qual-economic-roi">
                          <div><span>Investissement VIQ.AI renseigné</span><strong>{formatEur(report.economic_analysis.annual_viq_cost)}/an</strong></div>
                          <div><span>Gain net central estimatif</span><strong>{formatEur(report.economic_analysis.net_gain_central)}/an</strong></div>
                          <div><span>ROI central estimatif</span><strong>{report.economic_analysis.roi_pct != null ? `${report.economic_analysis.roi_pct}%` : "—"}</strong></div>
                          <div><span>Délai de retour estimatif</span><strong>{report.economic_analysis.payback_months != null ? `${report.economic_analysis.payback_months} mois` : "—"}</strong></div>
                        </div>
                      )}
                      <p className="aff-qual-economic-interpretation">{report.economic_analysis.interpretation}</p>
                      {!!report.economic_analysis.assumptions?.length && <div className="aff-qual-economic-assumptions"><strong>Hypothèses retenues</strong>{report.economic_analysis.assumptions.map((x,i)=><span key={i}>{x}</span>)}</div>}
                    </>
                  ) : (
                    <div className="aff-qual-economic-empty">
                      <strong>Chiffrage économique à compléter.</strong>
                      <p>Le diagnostic ne produit pas de montant lorsque les données de base sont absentes.</p>
                      {!!report.economic_analysis.missing_inputs?.length && <ul>{report.economic_analysis.missing_inputs.map((x,i)=><li key={i}>{x}</li>)}</ul>}
                    </div>
                  )}
                  <div className="aff-qual-economic-warning">Estimations indicatives fondées sur les données et hypothèses saisies. Elles ne constituent ni une garantie de gains, ni une prévision comptable ou financière.</div>
                </div>
              )}

              <div className="aff-qual-report-block">
                <div className="aff-qual-report-title">Pertinence de chaque module VehicleIQ / VIQ.AI</div>
                <div className="aff-qual-module-grid">
                  {modules.map((m,i) => (
                    <div key={m.id || i} className={`aff-qual-module ${levelClass(m.score)}`}>
                      <div className="aff-qual-module-head"><span>{m.name}</span><strong>{m.score}/100</strong></div>
                      <div className="aff-qual-module-level">Pertinence {m.level || scoreLabel(m.score)}</div>
                      <p>{m.rationale}</p>
                      {!!m.use_cases?.length && <div className="aff-qual-usecases">{m.use_cases.map((u,j) => <span key={j}>{u}</span>)}</div>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="aff-qual-report-grid">
                <div className="aff-qual-report-block"><div className="aff-qual-report-title">Angle d'approche recommandé</div><p>{report.approach_angle}</p></div>
                <div className="aff-qual-report-block"><div className="aff-qual-report-title">Formation</div><p>{report.training_recommendation}</p></div>
              </div>

              {!!report.deployment_plan?.length && <div className="aff-qual-report-block"><div className="aff-qual-report-title">Déploiement conseillé</div><ol>{report.deployment_plan.map((x,i) => <li key={i}>{x}</li>)}</ol></div>}
              {!!report.discovery_questions?.length && <div className="aff-qual-report-block"><div className="aff-qual-report-title">Questions à valider avant proposition</div><ul>{report.discovery_questions.map((x,i) => <li key={i}>{x}</li>)}</ul></div>}
              {!!report.risks?.length && <div className="aff-qual-report-block"><div className="aff-qual-report-title">Points de vigilance / informations manquantes</div><ul>{report.risks.map((x,i) => <li key={i}>{x}</li>)}</ul></div>}

              <div className="aff-qual-disclaimer">Le diagnostic s'appuie uniquement sur les informations saisies. Un signal public ou un alignement fonctionnel ne vaut pas besoin commercial confirmé ; les éléments non établis sont présentés comme questions ou hypothèses à valider. Les montants économiques sont des scénarios indicatifs et non des gains garantis.</div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
